import type { IBrandOpsAiPlaygroundResult, IBrandOpsConversation, IBrandOpsContract } from './types';

export const SLA_OVERDUE_MS = 24 * 60 * 60 * 1000;

export type BrandOpsAiReplyState =
    | { kind: 'reply'; text: string }
    | { kind: 'human_only'; message: string }
    | { kind: 'unconfigured'; message: string };

export const HUMAN_ONLY_MESSAGE = 'AI unavailable — reply as a human';

/** Plain text AI suggestion, or '' when AI is human-only / unavailable (never a stub). */
export function getAiSuggestedText(result?: IBrandOpsAiPlaygroundResult | null): string {
    if (!result || result.human_only) return '';
    const text = result.suggested_instagram_dm?.text || result.reply;
    return typeof text === 'string' ? text.trim() : '';
}

/** Classify a playground result so the UI never renders an empty/fake reply. */
export function getAiReplyState(result: IBrandOpsAiPlaygroundResult): BrandOpsAiReplyState {
    const text = getAiSuggestedText(result);
    if (result.human_only) {
        return { kind: 'human_only', message: result.message || 'Respond as a human — AI will not draft this one.' };
    }
    if (!text) {
        return {
            kind: result.configured ? 'human_only' : 'unconfigured',
            message: result.message || 'Respond as a human — no AI draft is available.',
        };
    }
    return { kind: 'reply', text };
}

/** Timestamp (ms) of the latest known activity for a conversation, or null. */
export function getConversationActivityMs(
    c: Pick<IBrandOpsConversation, 'updated_at' | 'last_message'>,
): number | null {
    const raw = c.updated_at || c.last_message?.created_at;
    if (!raw) return null;
    const ms = new Date(raw).getTime();
    return Number.isNaN(ms) ? null : ms;
}

/** "5m ago", "2h ago", "3d ago" */
export function formatRelativeAge(fromMs: number, nowMs: number = Date.now()): string {
    const diff = Math.max(0, nowMs - fromMs);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
}

/** Remaining time until SLA deadline, e.g. "3h left", or "2h overdue". */
export function formatSlaCountdown(
    activityMs: number,
    nowMs: number = Date.now(),
): {
    label: string;
    overdue: boolean;
    remainingMs: number;
} {
    const deadline = activityMs + SLA_OVERDUE_MS;
    const remainingMs = deadline - nowMs;
    const overdue = remainingMs < 0;
    const abs = Math.abs(remainingMs);
    const mins = Math.floor(abs / 60000);
    let clock: string;
    if (mins < 1) clock = overdue ? 'just now' : '<1m';
    else if (mins < 60) clock = `${mins}m`;
    else if (mins < 60 * 24) clock = `${Math.floor(mins / 60)}h`;
    else clock = `${Math.floor(mins / (60 * 24))}d`;
    return {
        overdue,
        remainingMs,
        label: overdue ? `${clock} overdue` : `${clock} left`,
    };
}

export function getConversationSla(
    c: Pick<IBrandOpsConversation, 'updated_at' | 'last_message'>,
    nowMs: number = Date.now(),
): { label: string; overdue: boolean } | null {
    const ms = getConversationActivityMs(c);
    if (ms == null) return null;
    return formatSlaCountdown(ms, nowMs);
}

type EscrowPeriod = NonNullable<IBrandOpsContract['escrow_periods']>[number];

const centsToMoney = (cents: number) => (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 });

/** "Paid $X USD (equiv of Y CUR)" when payout_* fields are present, else null. */
export function formatEscrowPayout(p: EscrowPeriod): string | null {
    if (p.payout_usd_cents == null) return null;
    const usd = `Paid $${centsToMoney(p.payout_usd_cents)} USD`;
    if (p.payout_contract_amount_cents != null && p.payout_contract_currency) {
        return `${usd} (equiv of ${centsToMoney(p.payout_contract_amount_cents)} ${p.payout_contract_currency})`;
    }
    return usd;
}
